import { Backdrop, CircularProgress, SxProps, Theme } from '@mui/material';
import { useContext, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import globalStyles from "../../assets/styles/global.module.scss";
import { UserContext, UserContextPayload } from '../../context/userContext';
import useGenericHook from '../../hooks/useGenericHook';
import { getTopOpenPopup } from '../../services/apiRequestLifecycle';

const GlobalRoundLoader = () => {
    
    const { flagLoading } = useContext(UserContext) as UserContextPayload;
    
    const { isScreenBelow600 } = useGenericHook();
    const isDesktop = !isScreenBelow600;
    
    const [sideNavOffset, setSideNavOffset] = useState(0);
    const [popupPaper, setPopupPaper] = useState<HTMLElement | null>(null);
    const [flagPopupOpen, setFlagPopupOpen] = useState(false);

    useLayoutEffect(() => {
        if (!flagLoading.flagRoundLoader || !isDesktop || flagLoading.flagFullScreenLoader) {
            setSideNavOffset(0);
            return;
        }

        const sideNav = document.querySelector('[data-side-nav-panel]');
        if (!sideNav) {
            setSideNavOffset(0);
            return;
        }

        const updateOffset = () => {
            setSideNavOffset(sideNav.getBoundingClientRect().width);
        };

        updateOffset();
        const resizeObserver = new ResizeObserver(updateOffset);
        resizeObserver.observe(sideNav);

        return () => resizeObserver.disconnect();
    }, [flagLoading.flagRoundLoader, isDesktop, flagLoading.flagFullScreenLoader]);

    // Loader started while a popup is open -> caused inside the popup, so it shows inside that popup only.
    // Popup opened while the loader is already running -> background (page) loading, so the loader stays behind the popup
    useLayoutEffect(() => {
        if (!flagLoading.flagRoundLoader) {
            setPopupPaper(null);
            setFlagPopupOpen(false);
            return;
        }

        const getTopPopupPaper = () => getTopOpenPopup()?.querySelector<HTMLElement>('.MuiDialog-paper') ?? null;

        const loaderPopupPaper = getTopPopupPaper();
        setPopupPaper(loaderPopupPaper);

        // Popup closed while its loading continues -> show it on the page
        const updatePopupState = () => {
            if (loaderPopupPaper && !loaderPopupPaper.isConnected) {
                setPopupPaper(null);
            }
            setFlagPopupOpen(Boolean(getTopPopupPaper()));
        };

        updatePopupState();
        // Popups are portaled to body, so this catches popups opening/closing while loading
        const mutationObserver = new MutationObserver(updatePopupState);
        mutationObserver.observe(document.body, { childList: true });

        return () => mutationObserver.disconnect();
    }, [flagLoading.flagRoundLoader]);

    if (!flagLoading.flagRoundLoader) {
        return null;
    }

    // Single loader markup for both page and popup; look comes from .backDropStyle, only positioning differs
    const renderLoader = (className: string, sx?: SxProps<Theme>) => (
        <Backdrop className={`${globalStyles.backDropStyle} ${className}`} sx={sx} open={true}>
            <CircularProgress color="inherit" size={50} />
        </Backdrop>
    );

    // createPortal renders the loader into the popup's DOM node (its MUI Dialog paper) instead of where GlobalRoundLoader is mounted,
    // so the overlay covers only the popup while staying in this component's React tree (keeps UserContext, unmounts with the loader)
    if (popupPaper) {
        return createPortal(renderLoader(globalStyles.popupLoaderOverlay), popupPaper);
    }

    // Background loading stays behind an open popup (MUI modal z-index 1300)
    return renderLoader("", {
        zIndex: flagPopupOpen ? 1299 : 1600,
        ...(isDesktop && sideNavOffset > 0 && !flagPopupOpen ? {
            left: `${sideNavOffset}px`,
            width: `calc(100% - ${sideNavOffset}px)`,
        } : {}),
    });
};

export default GlobalRoundLoader;
